import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataService } from './data.service';
import { environment } from 'src/environments/environment';

export interface EngageActivity {
  id?: string;
  patientId?: string;
  encounterId?: string | null;
  selectedDate?: string | null;
  type?: string;
  name?: string;
  comment?: string;
  stage?: string;
  createdAt?: string;
  activityBy?: string;
  notification?: number;
  [key: string]: any;
}

/** Response from GET /api/v1/notifications/counts */
export interface NotificationCountsResponse {
  newRequests: number;
  unreadMessages: number;
  missedCalls: number;
  newVoicemail: number;
  reminders: number;
  totalNotificationCounts: number;
}

/**
 * Prefer API fields that count only non-completed reminders; otherwise subtract completed from total when both exist.
 * Works for GET /notifications/counts and GET /referrals meta payloads.
 */
export function normalizeEngageReminderCountFromResponse(
  res: Record<string, unknown> | null | undefined
): number | undefined {
  if (res == null) {
    return undefined;
  }
  const num = (v: unknown): number | undefined => {
    if (v == null || v === "") {
      return undefined;
    }
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  const open =
    num(res["openReminderCount"]) ??
    num(res["open_reminder_count"]) ??
    num(res["pendingReminderCount"]) ??
    num(res["pending_reminders"]) ??
    num(res["activeReminders"]) ??
    num(res["active_reminders"]) ??
    num(res["incompleteReminders"]);
  if (open != null) {
    return open;
  }
  const total =
    num(res["reminderCount"]) ??
    num(res["reminders"]) ??
    num(res["totalReminders"]);
  const completed =
    num(res["completedReminderCount"]) ??
    num(res["completed_reminders"]) ??
    num(res["remindersCompleted"]) ??
    num(res["completedReminders"]);
  if (total != null && completed != null) {
    return Math.max(0, total - completed);
  }
  return total;
}

/** Response from GET /api/v1/dashboard/prospect */
export interface DashboardProspectResponse {
  totalCount: number;
  total_leads: number;
  /** PHI patient ids for dashboard Referrals tile → worklist filter. */
  total_leads_list?: string[];
  interested: number;
  scheduled: number;
  recovery: number;
}

/** Response from GET /api/v1/conversions */
export interface ConversionsResponse {
  totalReferrals: number;
  stageCounts: Record<string, number>;
  avgFirstResponseTime: number;
  avgBusinessHoursFirstResponseTimeFormatted: string;
  avgAppointmentLeadTime: number;
  avgAppointmentLeadTimeFormatted: string;
  conversionRate: number;
  percentageChange?: Record<string, number>;
}

export interface ReferralSourceByPatientIdsItem {
  patientId: string;
  referralId: string;
  /** Uuid string or `{ source, source_name }` from newer Engage APIs. */
  source: string | Record<string, unknown>;
}

export interface ReferralSourceByPatientIdsResponse {
  items: ReferralSourceByPatientIdsItem[];
}

export interface EngageActivityRequestOptions {
  /** When set, sent as `?isNurseNote=true|false` on activity list GETs. */
  isNurseNote?: boolean;
}

/** Hidden from GET `api/v1/activity/patient/{id}?isNurseNote=false` only. */
const PATIENT_ACTIVITY_EXCLUDED_WHEN_NOT_NURSE_NOTE = new Set(
  ["Referral Created", "Coverage - Primary", "Manual"].map((t) =>
    t.toLowerCase()
  )
);

function filterPatientActivitiesForNonNurseNoteTimeline(
  activities: EngageActivity[]
): EngageActivity[] {
  return activities.filter((activity) => {
    const type = String(activity.type ?? "")
      .trim()
      .toLowerCase();
    return !PATIENT_ACTIVITY_EXCLUDED_WHEN_NOT_NURSE_NOTE.has(type);
  });
}

@Injectable({
  providedIn: 'root'
})
export class EngageActivityService {
  /** PHI Engage-shaped activity lists (decrypted notes); Engage for writes and notifications. */
  private get activityListApiBaseUrl(): string {
    return environment.apiBaseUrl;
  }

  private get apiBaseUrl(): string {
    return environment.engageApiBaseUrl;
  }

  constructor(private readonly _dataService: DataService) {}

  getPatientActivities(
    patientId: string,
    filters?: readonly string[],
    options?: EngageActivityRequestOptions
  ): Observable<EngageActivity[]> {
    if (!patientId) {
      return new Observable(observer => {
        observer.next([]);
        observer.complete();
      });
    }
    const url = this.buildActivityListUrl(
      `${this.activityListApiBaseUrl}api/v1/activity/patient/${patientId}`,
      filters,
      options
    );
    return this._dataService.get(url).pipe(
      map((res: any) => {
        const list = this.normalizeActivityListResponse(res);
        if (options?.isNurseNote === false) {
          return filterPatientActivitiesForNonNurseNoteTimeline(list);
        }
        return list;
      })
    );
  }

  /**
   * Activities for a referral/lead before a clinic patientId exists.
   * Backend: GET api/v1/activity/{referralId} on PHI (Engage-compatible shape).
   */
  getReferralActivities(
    referralId: string,
    filters?: readonly string[],
    options?: EngageActivityRequestOptions
  ): Observable<EngageActivity[]> {
    if (!referralId) {
      return new Observable((observer) => {
        observer.next([]);
        observer.complete();
      });
    }
    const url = this.buildActivityListUrl(
      `${this.activityListApiBaseUrl}api/v1/activity/${referralId}`,
      filters,
      options
    );
    return this._dataService.get(url).pipe(
      map((res: any) => this.normalizeActivityListResponse(res))
    );
  }

  private buildActivityListUrl(
    baseUrl: string,
    filters?: readonly string[],
    options?: EngageActivityRequestOptions
  ): string {
    const list = (filters ?? [])
      .map((f) => String(f ?? "").trim())
      .filter(Boolean);
    const nurseNoteFilter = options?.isNurseNote;
    if (!list.length && nurseNoteFilter !== true && nurseNoteFilter !== false) {
      return baseUrl;
    }
    try {
      const u = new URL(baseUrl);
      for (const f of list) {
        u.searchParams.append("filter", f);
      }
      if (nurseNoteFilter === true || nurseNoteFilter === false) {
        u.searchParams.set("isNurseNote", nurseNoteFilter ? "true" : "false");
      }
      return u.toString();
    } catch {
      const parts: string[] = list.map(
        (f) => `filter=${encodeURIComponent(f)}`
      );
      if (nurseNoteFilter === true || nurseNoteFilter === false) {
        parts.push(`isNurseNote=${nurseNoteFilter ? "true" : "false"}`);
      }
      if (!parts.length) {
        return baseUrl;
      }
      const qs = parts.join("&");
      return baseUrl.includes("?") ? `${baseUrl}&${qs}` : `${baseUrl}?${qs}`;
    }
  }

  private normalizeActivityListResponse(res: any): EngageActivity[] {
    if (Array.isArray(res)) return res;
    if (res?.data && Array.isArray(res.data)) return res.data;
    if (res?.activities && Array.isArray(res.activities)) return res.activities;
    return res?.body ?? [];
  }

  resetNotifications(activityId: string, notification: 0 | 1): Observable<any> {
    const url = `${this.apiBaseUrl}api/v1/activity/reset-notifications/${activityId}`;
    return this._dataService.patch(url, { notification });
  }

  /**
   * Create a reminder activity via engage POST API.
   * Sends only description initially; user can edit and update via /api/v1/reminders.
   */
  postReminderActivity(patientId: string, description: string, name: string): Observable<any> {
    const url = `${this.apiBaseUrl}api/v1/activity`;
    const payload = {
      referralId: '',
      patientId,
      type: 'reminder',
      name,
      comment: description,
      stage: ''
    };
    return this._dataService.post(url, payload);
  }

  getReferralSourcesByPatientIds(patientIds: string[]): Observable<ReferralSourceByPatientIdsResponse> {
      const ids = (patientIds || []).map((x) => String(x || '').trim()).filter(Boolean);
      if (!ids.length) {
        return new Observable((observer) => {
          observer.next({ items: [] });
          observer.complete();
        });
      }
      const url = `${this.apiBaseUrl}api/v1/referrals/referral-sources/by-patient-ids`;
      return this._dataService.post(url, { patientIds: ids });
    }
  /**
   * GET /api/v1/notifications/counts
   * @param startDate - ISO-8601 with offset (e.g. 2026-03-29T00:00:00.000+05:00)
   * @param endDate - ISO-8601 with offset (e.g. 2026-04-04T23:59:59.999+05:00)
   */
  getNotificationCounts(startDate: string, endDate: string): Observable<NotificationCountsResponse> {
    const sd = (startDate ?? "").trim();
    const ed = (endDate ?? "").trim();
    const params = new URLSearchParams();
    if (sd) params.set("startDate", sd);
    if (ed) params.set("endDate", ed);
    const qs = params.toString();
    const url = `${this.apiBaseUrl}api/v1/notifications/counts${qs ? `?${qs}` : ""}`;
    return this._dataService.get(url).pipe(
      map((res: any) => {
        const raw = res as Record<string, unknown>;
        const n = normalizeEngageReminderCountFromResponse(raw);
        const reminders = n ?? Number(raw?.reminders) ?? 0;
        return { ...res, reminders } as NotificationCountsResponse;
      })
    );
  }

  /**
   * GET /api/v1/dashboard/prospect
   * @param startDate - ISO date string (e.g. 2026-02-28T00:00:00.000Z)
   * @param endDate - ISO date string (e.g. 2026-03-06T23:59:59.999Z)
   * @param referralSources - Optional referral source ids (`referralSource` query param; comma-separated if multiple)
   * @param providers - Optional provider resource ids (comma-separated)
   * @param locations - Optional location names (comma-separated)
   */
  getDashboardProspect(
    startDate: string,
    endDate: string,
    referralSources?: string[],
    providers?: string[],
    locations?: string[]
  ): Observable<DashboardProspectResponse> {
    const params = new URLSearchParams({
      startDate,
      endDate
    });
    if (referralSources?.length) {
      params.set("referralSource", referralSources.join(","));
    }
    if (providers?.length) {
      params.set("providers", providers.join(","));
    }
    if (locations?.length) {
      params.set("locations", locations.join(","));
    }
    const url = `${this.apiBaseUrl}api/v1/dashboard/prospect?${params.toString()}`;
    return this._dataService.get(url);
  }

  /**
   * GET /api/v1/conversions (Lead Time widget)
   * @param startDate - ISO-8601 with offset (e.g. from Luxon `toISO()`), same as notifications/counts
   * @param endDate - ISO-8601 with offset
   * @param timeZone - Optional IANA zone (e.g. from `Intl.DateTimeFormat().resolvedOptions().timeZone`), same pattern as referrals counts
   * @param referralSource - Optional single selected source id; query param `referralSource` (omit when "All Sources")
   * @param providers - Optional provider resource ids (comma-separated)
   * @param locations - Optional location names (comma-separated)
   */
  getConversions(
    startDate: string,
    endDate: string,
    timeZone?: string,
    referralSource?: string,
    providers?: string[],
    locations?: string[]
  ): Observable<ConversionsResponse> {
    const params = new URLSearchParams({
      startDate,
      endDate,
    });
    if (timeZone && String(timeZone).trim() !== "") {
      params.set("timeZone", String(timeZone).trim());
    }
    if (referralSource && String(referralSource).trim() !== "") {
      params.set("referralSource", String(referralSource).trim());
    }
    if (providers?.length) {
      params.set("providers", providers.join(","));
    }
    if (locations?.length) {
      params.set("locations", locations.join(","));
    }
    const url = `${this.apiBaseUrl}api/v1/conversions?${params.toString()}`;
    return this._dataService.get(url);
  }

  /**
   * GET /api/v1/referrals/clinic - List all engage referrals/leads for the clinic.
   * Returns array of referral objects with patientId, firstName, lastName, stage, lead_status, last_activity, etc.
   * Supports pagination: 20 per page.
   * @param page - 1-based page number
   * @param limit - items per page (default 20)
   */
  getEngageReferrals(page: number = 1, limit: number = 30): Observable<EngageReferralsResponse> {
    const params = new URLSearchParams({
      page: String(page),
      limit: String(limit)
    });
    const url = `${this.apiBaseUrl}api/v1/referrals?${params.toString()}`;
    return this._dataService.get(url).pipe(
      map((res: any) => {
        const referrals = Array.isArray(res)
          ? res
          : res?.data && Array.isArray(res.data)
            ? res.data
            : res?.referrals && Array.isArray(res.referrals)
              ? res.referrals
              : res?.body ?? [];
        const total = res?.total ?? res?.totalCount ?? res?.pagination?.total ?? res?.meta?.total ?? null;
        const hasMore =
          total != null
            ? page * limit < total
            : referrals.length >= limit;
        return { referrals, total, page, hasMore };
      })
    );
  }
}



/** Response from paginated GET /api/v1/referrals (aligned with wa-engage-v2 referral list). */
export interface EngageReferralsResponse {
  referrals: EngageReferral[];
  total?: number;
  page: number;
  hasMore: boolean;
  missedCount?: number;
  smsCount?: number;
  voicemailCount?: number;
  reminderCount?: number;
  newLeadsCount?: number;
  totalNotifications?: number;
}

/** Last activity nested in referral from API */
export interface EngageReferralLastActivity {
  id?: string;
  type?: string;
  stage?: string | null;
  comment?: string;
  createdDateTime?: string;
  name?: string | null;
  [key: string]: any;
}

/** Engage referral/lead from API */
export interface EngageReferral {
  id?: string;
  patientId?: string;
  /** Some `GET /api/v1/referrals` payloads expose patient ids here instead of {@link patientId}. */
  patientIds?: string[];
  assigned_to?: string | null;
  /** Newer API: assignee id (may mirror assigned_to) */
  assign_to?: string | Record<string, unknown> | null;
  assignTo?: string | Record<string, unknown> | null;
  assign_to_name?: string;
  /** Referral source id (new list API) or nested object */
  source?: string | Record<string, unknown>;
  /** GET /api/v1/referrals may return `{ source, source_name }` instead of top-level `source`. */
  referral_source?: string | { source?: string; source_name?: string; sourceName?: string } | Record<string, unknown>;
  referralSource?: string | Record<string, unknown>;
  source_name?: string;
  channel?: string;
  createdAt?: string;
  dob?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  gender?: string;
  isPhoneVerified?: boolean;
  last_activity?: EngageReferralLastActivity;
  /**
   * Client-only: ISO datetime of the earliest merged timeline item (global notes feed).
   * Used only for the referrals list activity-date column — icons/summary still use {@link last_activity}.
   */
  engageListFirstActivityIso?: string;
  lead_status?: string;
  /** Account / Engage queue row id when referral exposes it */
  que_id?: string;
  queId?: string;
  queue_id?: string;
  queueId?: string;
  queue_name?: string;
  que_name?: string;
  queueName?: string;
  /** Workflow stage row id (`GET /work-flows/:id/stages` `id`). */
  stage_id?: string;
  stageId?: string;
  /** Workflow stage display name from referral / directory payload. */
  stage_name?: string;
  stageName?: string;
  notification_count?: number;
  phone?: string;
  phone_mask?: string;
  reminder_count?: number;
  reminder_notification_count?: number;
  stage?: string;
  starred?: number;
  [key: string]: any;
}

